import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { get as _get } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import ConfirmationDialog from '../confirmationdlg';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import HealthIcon from '@mui/icons-material/Favorite';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/PublishedWithChanges';

import BulletChart from '../../components/charts/bulletchart';

import SensorService from '../../data/sensors-service';
import Auth from '../../data/auth-service';
import { purple } from '../../data/colors';

import { FriendlyPolicyName } from '../../libraries/helpers';

const useStyles = makeStyles(theme => ({
    flexFix: {
        display: 'flex',
    },
    status: {
        height: '100%',
        width: 25,
        display: 'inline-block',
        borderTopLeftRadius: 3,
        borderBottomLeftRadius: 3,
    },
    statusIcons: {
        marginTop: theme.spacing(3),
        '& .MuiSvgIcon-root': {
            marginBottom: theme.spacing(1),
            cursor: 'pointer',
        },
    },
    cardContainer: {
        width: 'calc(100% - 35px)',
        maxWidth: 700,
        display: 'inline-block',
        height: '100%',
    },
    card: {
        width: '100%',
        maxWidth: 700,
        height: '100%',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    policy: {
        // background: '#388298',
        color: theme.palette.common.white,
        borderRadius: 5,
        display: 'flex',
        margin: 2,
        padding: '5px 7px',
    },
    good: {
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
    },
    warn: {
        background: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
    },
    bad: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
    goodText: {
        color: theme.palette.success.main,
        paddingTop: 10,
    },
    warnText: {
        color: theme.palette.warning.main,
        background: theme.palette.mode === 'light' ? '#1E1E1E' : 'none',
        marginTop: 5,
        marginLeft: '3px',
        padding: '5px 5px 5px 5px',
        borderRadius: '3px',
    },
    badText: {
        color: theme.palette.error.main,
        paddingTop: 5,
    },
    clearFix: {
        '&:after': {
            content: '',
            display: 'table',
            clear: 'both',
        }
    },
    tooltip: {
        color: 'white',
    },
}));

const SensorCard = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    // const navigate = useNavigate();
    const [metrics, setMetrics] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [reregisterOpen, setReregisterOpen] = useState(false);
    const sensorPerms = Auth.permissions.get('Sensors');

    const handlePolicySelect = (e, type, policy) => {
        e.stopPropagation();
        props.handlePolicySelect(type, policy);
    };

    const jobColor = v => {
        const i = policies.findIndex(x => x.type === v);
        if (i !== -1) {
            const j = policies[i].policies.findIndex(y => y.name === props.sensor.data.jobs[v].name);
            if (j !== -1) {
                if (Object.prototype.hasOwnProperty.call(policies[i].policies[j], 'color')) {
                    return policies[i].policies[j].color;
                }
            }
        }
        return '#388298';
    };

    useEffect(() => {
        const metrics = [
            {
                label: 'CPU',
                min: 0,
                max: Object.values(_get(props.sensor, 'health_metrics.system.cpuinfo.total', {}))
                    .reduce((a, c) => a + c, 0),
                value: Object.entries(_get(props.sensor, 'health_metrics.system.cpuinfo.total', {}))
                    .filter(([k, v]) => k !== 'idle')
                    .reduce((a, [k, v]) => a + v, 0),
            },
            {
                label: 'Memory',
                min: 0,
                max: _get(props.sensor, 'health_metrics.system.meminfo.total_memory', 0),
                value: _get(props.sensor, 'health_metrics.system.meminfo.total_memory', 0) -
                    _get(props.sensor, 'health_metrics.system.meminfo.avail_memory', 0),
            },
            {
                label: 'Disk',
                min: 0,
                max: (_get(props.sensor, 'health_metrics.disks') || [])
                    .find(d => d.mount_point === '/') ? 100 : 0,
                value: 100 * ((_get(props.sensor, 'health_metrics.disks') || [])
                    .find(d => d.mount_point === '/') || { utilization: 0 }).utilization,
                pct: true,
            },
        ];
        setMetrics(metrics);
    }, [props.sensor]);

    useEffect(() => {
        setPolicies(props.policies || []);
    }, [props]);

    const checkDeliveryStatus = () => {
        if (props.sensor && props.sensor.deliveries) {
            if (Object.keys(props.sensor.deliveries).map((v, i) => {
                if (typeof props.sensor.deliveries[v] === 'object' || v !== 'api') {
                    return v;
                }
                return null;
            }).filter(v => v).filter(v => !_get(props, `sensor.deliveries[${v}].success`, false)).length > 3) {
                return (
                    <Box ml={1} className={classes.badText}>
                        Multiple delivery failures
                    </Box>
                );
            }
            return Object.keys(props.sensor.deliveries).map((v, i) => {
                if (typeof props.sensor.deliveries[v] === 'object' || v !== 'api') {
                    if (!_get(props, `sensor.deliveries[${v}].success`, false)) {
                        if (_get(props, `sensor.deliveries[${v}].message`, '') === 'In Progress') {
                            return null;
                        } else {
                            return (
                                <Box key={'delivery-failure-' + i} ml={1} className={classes.badText}>
                                    {FriendlyPolicyName(v)} delivery failed
                                </Box>
                            );
                        }
                    }
                }
                return null;
            }).filter(v => v);
        }
        return [];
    };

    const handleReregisterSensor = () => {
        setReregisterOpen(false);
        SensorService.reregisterSensor(props.sensor.host).then((res) => {
            toast.success('Sensor resynch operation complete. Please allow time for health polling and policy change deliveries to complete.');
        }, err => {
            toast.error(err);
        }); 
    };
    const handleOpenReregister = () => {
        setReregisterOpen(true);
    };
    const handleCloseReregister = () => {
        setReregisterOpen(false);
    };
    return (
        <Grid item md={props.colsize || 3} className={classes.flexFix}>
            <Box className={clsx(classes.status, {
                [classes.good]: (props.sensor.status === 'normal'),
                [classes.warn]: (props.sensor.status === 'warning'),
                [classes.bad]: (props.sensor.status === 'critical')
            })}>
                <Box className={classes.statusIcons} display={props.mini ? 'none' : 'block'}>
                    <Tooltip title=" Health " placement="right" followCursor>
                    <HealthIcon aria-label="sensor health" onClick={props.handleSelect}/></Tooltip>
                    <Tooltip title=" Settings " placement="right" followCursor>
                    {(sensorPerms.write || sensorPerms.delete || sensorPerms.admin) && <SettingsIcon aria-label="sensor settings" onClick={props.handleUpdate} />}</Tooltip>
                    <Tooltip title=" Reregister " placement="right" followCursor>
                    {(sensorPerms.write || sensorPerms.delete || sensorPerms.admin) && <RefreshIcon aria-label="sensor reregister"  onClick={() => { handleOpenReregister()} } />}</Tooltip>
                </Box>
            </Box>
            <Box className={classes.cardContainer}>
                <Card className={classes.card}>
                    <Box p={2}>
                        <Box className={classes.header}>
                            <Typography gutterBottom variant="h6">{props.sensor.data.name}</Typography>
                            <Box display="flex">
                                {_get(props.sensor, 'health_metrics.appliance_type', 'sensor') === 'btsensor' &&
                                    <IconButton size='small' style={{
                                        background: purple[700],
                                        minWidth: '30px',
                                        height: '30px',
                                        marginTop: '-4px',
                                        marginRight: '5px',
                                    }}>
                                        <Typography variant="subtitle2" color={theme.palette.primary.contrastText}>BT</Typography>
                                    </IconButton>
                                }
                                <Typography variant="subtitle2">{props.sensor.model}</Typography>
                            </Box>
                        </Box>
                        <Typography variant="subtitle2">{props.sensor.host}</Typography>

                        { _get(props.sensor, 'data.instance_id') &&
                            <Box>
                                <Typography variant="subtitle2">
                                    {_get(props.sensor, 'data.asg_name')}
                                </Typography>
                                <Typography variant="subtitle2">
                                    {_get(props.sensor, 'data.instance_id')}
                                </Typography>
                                <Typography variant="subtitle2">
                                    {_get(props.sensor, 'data.avail_zone')}
                                </Typography>
                                <Typography variant="subtitle2">
                                    {_get(props.sensor, 'data.instance_state')}
                                </Typography>
                            </Box>}

                        <Typography variant="subtitle2">OpenText™ NDR Version: {props.sensor.health_metrics && props.sensor.health_metrics.version}</Typography>
                        {/* don't show suricata/zeek version for backtesting sensor + space holder */}
                        <React.Fragment>
                            <Typography variant="subtitle2">{
                                _get(props.sensor, 'health_metrics.appliance_type', 'sensor') === 'sensor'
                                    ? `Suricata Version: ${_get(props.sensor, 'health_metrics.suricata_version', 'N/A')}`
                                    : <br/>}</Typography>
                            <Typography variant="subtitle2">{
                                _get(props.sensor, 'health_metrics.appliance_type', 'sensor') === 'sensor'
                                    ? `Zeek Version: ${_get(props.sensor, 'health_metrics.zeek_version', 'N/A')}`
                                    : <br/>}</Typography>
                        </React.Fragment>
                        <Box my={2} display={(() => {
                            if (process.env.BRICATA_APPLIANCE_TYPE === 'mc2') {
                                return 'none';
                            }

                            return props.sensor.deliveries && Object.keys(props.sensor.deliveries).filter(v => {
                                return v !== 'api' && props.sensor.deliveries[v].name !== 'default';
                            }).length ? 'flex' : 'none';
                        })()} flexWrap="wrap">
                            {props.sensor.data && props.sensor.data.jobs && Object.keys(props.sensor.data.jobs).map((v, i) => {
                                if (props.sensor.data.jobs[v].name !== 'default' && v !== 'btconf') {
                                    const color = jobColor(v);
                                    return (
                                        <Tooltip
                                            key={'sensor-card-policy-' + v + '-' + i}
                                            title={FriendlyPolicyName(v)}
                                            classes={{ tooltip: classes.tooltip }}
                                        >
                                            <div
                                                className={classes.policy}
                                                onClick={(e) => { handlePolicySelect(e, v, props.sensor.data.jobs[v].name) }}
                                                style={{
                                                    background: color,
                                                    color: theme.palette.getContrastText(color)
                                                }}
                                            >
                                                {props.sensor.data.jobs[v].name}
                                            </div>
                                        </Tooltip>
                                    );
                                }
                                return null;
                            })}
                        </Box>
                        <Box display={(props.small || props.mini) ? 'none' : 'block'}>
                            {metrics.map(m => (
                                <Box mb={2} style={{ width: '100%' }} key={m.label}>
                                    <BulletChart
                                        label={m.label}
                                        min={m.min}
                                        max={m.max}
                                        value={m.value}/>
                                </Box>
                            ))}
                        </Box>
                        <Box mt={2} display={props.mini ? 'none' : 'flex'} flexWrap="wrap">
                            {props.sensor.health_metrics && props.sensor.health_metrics.issues && props.sensor.health_metrics.issues.map((v, i) => {
                                return <Box ml={1} key={'sensor-issue-' + i} className={clsx({
                                    [classes.goodText]: props.sensor.status === 'normal',
                                    [classes.warnText]: props.sensor.status === 'warning',
                                    [classes.badText]: props.sensor.status === 'critical',
                                })}>{v.message}</Box>;
                            })}
                            {props.sensor.health_metrics && !Object.prototype.hasOwnProperty.call(props.sensor.health_metrics, 'issues') && props.sensor.health_status && (() => {
                                return <Box ml={1} className={clsx({
                                    [classes.goodText]: props.sensor.health_status.status === 'normal',
                                    [classes.warnText]: props.sensor.health_status.status === 'warning',
                                    [classes.badText]: props.sensor.health_status.status === 'critical',
                                })}>{props.sensor.health_status.msg}</Box>;
                            })()}
                            {checkDeliveryStatus()}
                        </Box>
                        <Box mt={1} display={props.children ? 'block' : 'none'}>
                            {props.children || null}
                        </Box>
                    </Box>
                </Card>
                <ConfirmationDialog
                    onClose={handleCloseReregister}
                    onOK={handleReregisterSensor}
                    open={reregisterOpen}
                    message={(() => {
                        return (
                            <Box>
                                NOTICE: This is a disruptive but non-destructive action while the CMC resynchronizes the
                                sensor's certificate and sensor services restart to begin utilizing the updated certificate. 
                                <Box>
                                    Please confirm or cancel the reregistration action for sensor   
                                    [<Box className={classes.dialogHighlight} display="inline-block" mx={1}>
                                    {props.sensor && props.sensor.host} </Box>].
                                </Box>
                            </Box>
                        );
                    })()}
                    title="Reregister Sensor"
                    cancelLabel="Cancel"
                    okLabel="Confirm"
                />
            </Box>
        </Grid>
    );
};

SensorCard.propTypes = {
    sensor: PropTypes.object.isRequired,
    handlePolicySelect: PropTypes.func,
    handleSelect: PropTypes.func,
    handleUpdate: PropTypes.func,
    small: PropTypes.bool,
    mini: PropTypes.bool,
    colsize: PropTypes.number,
    policies: PropTypes.array,
    children: PropTypes.node,
};

export default SensorCard;
